import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Platform, AlertController } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Preferences } from '@capacitor/preferences';

import { OnesignalService } from './services/one-signal.service';

import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  constructor(
    private translate: TranslateService,
    private platform: Platform,
    private alertController: AlertController,
    private router: Router,
    private location: Location,
    private oneSignal: OnesignalService
  ) {
    this.initializeApp();
  }

  async initializeApp() {
    this.injectGetProjectButton();
    const { value } = await Preferences.get({ key: 'user-lang' });
    const savedLang = value || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(savedLang);
    this.oneSignal.OneSignalInit();
    document.documentElement.dir = savedLang === 'ar' ? 'rtl' : 'ltr';
    this.initializeBackButtonCustomHandler();
  }

  initializeBackButtonCustomHandler() {
    this.platform.backButton.subscribeWithPriority(0, () => {
      // Only show the exit confirmation if we are on root pages
      const currentUrl = this.router.url;
      if (currentUrl === '/home' || currentUrl === '/login' || currentUrl === '/') {
        this.showExitConfirmation();
      } else {
        // If not on a root page, let the default back navigation handle it
        this.location.back();
      }
    });
  }

  async showExitConfirmation() {
    const alert = await this.alertController.create({
      header: 'Exit App',
      message: 'Are you sure you want to exit the app?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Yes',
          handler: () => {
            App.exitApp();
          }
        }
      ]
    });
    await alert.present();
  }

  injectGetProjectButton() {
    if (!(environment as any).defaultLogin?.projectButton || !(environment as any).projectUrl) return;

    const buttonId = 'get-full-project-btn';
    if (document.getElementById(buttonId)) return;

    const button = document.createElement('a');
    button.id = buttonId;
    button.href = (environment as any).projectUrl;
    button.target = '_blank';
    button.innerHTML = `
      <img src="assets/icon/favicon.png" style="width: 24px; height: 24px;" onerror="this.src='https://pegasus-superapp-monorepo.vercel.app/favicon.ico'">
      <span>Get full project</span>
    `;

    const style = document.createElement('style');
    style.innerHTML = `
      #${buttonId} {
        position: fixed;
        bottom: 85px;
        right: 20px;
        background: linear-gradient(135deg, #FFD700, #FFA500);
        color: #000;
        padding: 12px 20px;
        border-radius: 50px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.3);
        z-index: 10000;
        text-decoration: none;
        font-weight: 800;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        animation: float-btn 3s ease-in-out infinite;
        border: 2px solid #fff;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      #${buttonId}:hover {
        transform: scale(1.1) translateY(-5px);
        box-shadow: 0 15px 35px rgba(0,0,0,0.4);
        background: linear-gradient(135deg, #FFA500, #FFD700);
      }
      #${buttonId}:active {
        transform: scale(0.95);
      }
      @keyframes float-btn {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @media (max-width: 768px) {
        #${buttonId} span { display: none; }
        #${buttonId} { padding: 12px; border-radius: 50%; bottom: 80px; }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(button);
  }
}
